import MISC from '../Monitor/images/misc.png';
import CAR from '../Monitor/images/car.png';
import PEDESTRIAN from '../Monitor/images/PEDESTRIAN.png';
import CYCLIST from '../Monitor/images/CYCLIST.png';
import TRUCK from '../Monitor/images/TRUCK.png';
import BUS from '../Monitor/images/BUS.png';
import TRAFFIC_CONE from '../Monitor/images/traffic-cone.png';
import ROAD_SEWER from '../Monitor/images/road_sewer.png';
import NONE from '../Monitor/images/NONE.png';
import TRICYCLE from '../Monitor/images/TRICYCLE.png';
import POLE from '../Monitor/images/pole.png';
import JD_VEHICLE from '../Monitor/images/jd_vehicle.png';
import OTHER_VEHICLE from '../Monitor/images/other_vehicle.png';
import BRAKE from '../Monitor/images/brake.png';
import START from '../Monitor/images/start.png';
import END from '../Monitor/images/end.png';
import TRAFFIC_LIGHT_MAIN_BAKED from '../Monitor/images/traffic_light_main_baked.png';
import TRAFFIC_LIGHT_GREY_BAKED from '../Monitor/images/traffic_light_grey_baked.png';
import TRAFFIC_LIGHT_PASS_BAKED from '../Monitor/images/traffic_light_pass_baked.png';
import { TrafficLightColorType } from './types';

// 转换函数
const toRGB = (hex: number) => [
  ((hex >> 16) & 255) / 255,
  ((hex >> 8) & 255) / 255,
  (hex & 255) / 255,
];

export const COLOR_MAP: Record<number, number[]> = {
  0: toRGB(0x999999),
  1: toRGB(0x2374de),
  2: toRGB(0xd56f79),
  3: toRGB(0xedb936),
  4: toRGB(0x5b9546),
  5: toRGB(0xaa6543),
  6: toRGB(0x999999),
  7: toRGB(0x999999),
  8: toRGB(0xee9818),
  9: toRGB(0x18e3ee),
  10: toRGB(0xee18d3),
  11: toRGB(0xffffff),
  12: toRGB(0xffffff),
  13: toRGB(0xab91e0),
  14: toRGB(0xedb936),
  15: toRGB(0xff0000),
  16: toRGB(0x3dae58),
  17: toRGB(0xd4237a),
  18: toRGB(0xf7a83f),
};

// 宽线样式配置，用于 MeshPool 初始化不同材质属性
export const WIDE_LINE_MAT_CONFIGS: Record<
  string,
  {
    rgb: [number, number, number];
    opacity: number;
    transparent: boolean;
    width: number;
    zOffset: number;
  }
> = {
  planning_path: {
    rgb: [0.27, 0.92, 0.77], // 对应 0x46eac5
    opacity: 0.8,
    transparent: false,
    width: 0.2,
    zOffset: 0.1,
  },
  planning_trajectory: {
    rgb: [0.09, 0.78, 0.62], // 对应 0x16c79f
    opacity: 0.3,
    transparent: true,
    width: 1.0,
    zOffset: 0.08,
  },

  // Gate 宽线（按类型配色，与 rover-op-ui 最终版一致）
  gate_auto: {
    rgb: [0, 0, 1], // 对应 0x0000ff（AUTO_GATE 蓝色）
    opacity: 1.0,
    transparent: false,
    width: 0.4,
    zOffset: 0.05,
  },
  gate_manual: {
    rgb: [0, 0, 1], // 对应 0x0000ff（MANUAL_GATE 蓝色）
    opacity: 1.0,
    transparent: false,
    width: 0.4,
    zOffset: 0.05,
  },
  gate_barrier: {
    rgb: [0x80 / 255, 0, 0x80 / 255], // 对应 0x800080（BARRIER_GATE 紫色）
    opacity: 1.0,
    transparent: false,
    width: 0.4,
    zOffset: 0.05,
  },
  gate_unknown: {
    rgb: [1, 1, 0], // 对应 0xffff00（UNKNOWN_TYPE 黄色）
    opacity: 1.0,
    transparent: false,
    width: 0.4,
    zOffset: 0.05,
  },
};

// Gate 类型 -> 宽线 key 映射（worker 使用，key 为 proto 中 Gate.Type 枚举值）
export const GATE_TYPE_WIDE_LINE_KEY: Record<number, string> = {
  0: 'gate_unknown', // UNKNOWN_TYPE
  1: 'gate_auto', // AUTO_GATE
  2: 'gate_manual', // MANUAL_GATE
  3: 'gate_barrier', // BARRIER_GATE
};
// 1. 定义枚举（对应 Proto 中的数值）
export enum LineType {
  DOT = 0,
  DASH = 1,
  SOLID = 2,
}

export enum LineColor {
  OTHER = 0,
  YELLOW = 1,
  WHITE = 2,
}

// 2. 窄线配置 (Key 格式: "Type_Color")
export const NARROW_LINE_CONFIGS: Record<
  string,
  {
    dashed: boolean;
    dashSize?: number;
    gapSize?: number;
    rgb?: number[];
    zOffset?: number;
  }
> = {
  // 基础样式
  [`${LineType.SOLID}_${LineColor.WHITE}`]: { dashed: false, rgb: [1, 1, 1] },
  [`${LineType.SOLID}_${LineColor.YELLOW}`]: { dashed: false, rgb: [1, 1, 0] },
  [`${LineType.SOLID}_${LineColor.OTHER}`]: {
    dashed: false,
    rgb: [0.4, 0.4, 0.4],
  },

  // 虚线/点线
  [`${LineType.DASH}_${LineColor.WHITE}`]: {
    dashed: true,
    dashSize: 1.0,
    gapSize: 1.0,
    rgb: [1, 1, 1],
  },
  [`${LineType.DASH}_${LineColor.YELLOW}`]: {
    dashed: true,
    dashSize: 1.0,
    gapSize: 1.0,
    rgb: [1, 1, 0],
  },
  [`${LineType.DASH}_${LineColor.OTHER}`]: {
    dashed: true,
    dashSize: 1.0,
    gapSize: 1.0,
    rgb: [0.4, 0.4, 0.4],
  },
  [`${LineType.DOT}_${LineColor.WHITE}`]: {
    dashed: true,
    dashSize: 0.2,
    gapSize: 0.2,
    rgb: [1, 1, 1],
  },
  [`${LineType.DOT}_${LineColor.YELLOW}`]: {
    dashed: true,
    dashSize: 0.2,
    gapSize: 0.2,
    rgb: [1, 1, 0],
  },
  [`${LineType.DOT}_${LineColor.OTHER}`]: {
    dashed: true,
    dashSize: 0.2,
    gapSize: 0.2,
    rgb: [0.4, 0.4, 0.4],
  },

  // 特殊业务 key (不属于 boundary 的可以手动定义)
  routing: { dashed: false, rgb: [1, 0, 0], zOffset: 0.05 }, // 路径规划
  // 车道中心线（Origin 使用 0x339900）
  laneCenter: { dashed: false, rgb: [51 / 255, 153 / 255, 0], zOffset: 0.01 },

  // 障碍物轨迹：单独的 key，默认不虚线
  obstacleTrajectory: {
    dashed: false,
    zOffset: 0.1,
  },

  // stopLine：独立 key
  stopLine: { dashed: false, rgb: [1, 1, 1], zOffset: 0.02 },

  // 红绿灯定位虚线（用于从图标位置指向地面锚点）
  trafficLightGuideDash: {
    dashed: true,
    dashSize: 0.5,
    gapSize: 0.5,
    rgb: [0xc9 / 255, 0xc9 / 255, 0xc9 / 255],
    zOffset: 0,
  },

  // parkingGoal：停车目标（红色箭头 + 绿色矩形框）
  parkingGoalArrow: { dashed: false, rgb: [1, 0, 0], zOffset: 0.25 },
  parkingGoalBox: {
    dashed: false,
    rgb: [0x16 / 255, 0xc7 / 255, 0x9f / 255],
    zOffset: 0.2,
  },
};

// 3. 获取样式的辅助工具 (Worker 使用)
export function getNarrowStyle(
  type?: number | null,
  color?: number | null,
  customKey?: string
) {
  const key = customKey || `${type}_${color}`;

  // 如果配置里没有这个组合，降级 config和key到默认的 SOLID_WHITE
  const config = NARROW_LINE_CONFIGS[key];
  if (config) return { key, config };
  const fallbackKey = `${LineType.SOLID}_${LineColor.WHITE}`;
  return { key: fallbackKey, config: NARROW_LINE_CONFIGS[fallbackKey] };
}

// 配置：不同 Lidar 的颜色显示
const LIDAR_COLORS = {
  DEFAULT: [0.5, 0.5, 0.5],
  LIDAR_FRONT: [1.0, 0.3, 0.3], // 红色
  LIDAR_REAR: [0.3, 1.0, 0.3], // 绿色
};

export function getLidarColor(name: string) {
  return (
    LIDAR_COLORS[name as keyof typeof LIDAR_COLORS] || LIDAR_COLORS.DEFAULT
  );
}

// 障碍物icon纹理路径配置（根据 Proto 中的 ObstacleType 数值索引）
export const texturePaths: (string | null)[] = [
  MISC, // MISC = 0
  CAR, // CAR = 1
  PEDESTRIAN, // PEDESTRIAN = 2
  CYCLIST, // CYCLIST = 3
  TRUCK, // TRUCK = 4
  BUS, // BUS = 5
  MISC, // POINT_CLOUD_CLUSTER = 6
  MISC, // ARTIFICIAL_VARIABLE_OBJECT = 7
  TRAFFIC_CONE, // TRAFFIC_CONE = 8
  MISC, // LOW_PROFILE_OBSTACLE = 9
  ROAD_SEWER, // ROAD_SEWER = 10
  NONE, // STATIC_NON_MOTOR = 11
  NONE, // WATER_BARRIER = 12
  TRICYCLE, // TRICYCLE = 13
  CYCLIST, // CYCLIST_STOPPED = 14
  POLE, // POLE = 15
  JD_VEHICLE, // JD_VEHICLE = 16
  OTHER_VEHICLE, // OTHERS_VEHICLE = 17
  BRAKE, // IMAGE_OCC = 18
  null, // reserved 19
  null, // reserved 20
];

export const LINE_ICON_MAX = 10; // 预分配若干个起/终点（通常 1 个就够，但保守预留）

export const lineIconPaths: Record<string, string | null> = {
  start: START,
  end: END,
};

// 交通灯图标通常较大，预分配更多的图标槽位以适应不同状态的显示需求
export const TRAFFIC_LIGHT_ICON_MAX = 10;

// 交通灯图标：根据状态显示不同的图标（主视、灰色、通过）
export const trafficLightIconPaths: Record<string, string | null> = {
  main: TRAFFIC_LIGHT_MAIN_BAKED,
  grey: TRAFFIC_LIGHT_GREY_BAKED,
  pass: TRAFFIC_LIGHT_PASS_BAKED,
};

// 文本相对于红绿灯锚点的默认高度偏移
export const TRAFFIC_LIGHT_LABEL_Z_OFFSET = 10;

// 红绿灯 label 的“逻辑画布尺寸”（CSS 像素）。实际 canvas 纹理会乘以 DPR 以提高清晰度。
export const TRAFFIC_LIGHT_LABEL_CANVAS_W = 320;
export const TRAFFIC_LIGHT_LABEL_CANVAS_H = 200;

// 红绿灯的颜色映射（用于根据状态设置 label 颜色）
export const trafficLightTypeColorMap: Record<TrafficLightColorType, string> = {
  [TrafficLightColorType.PASS]: '#00ff00',
  [TrafficLightColorType.MAIN]: '#ff0000',
  [TrafficLightColorType.GREY]: '#10E8F0',
};
